import { useCallback } from 'react';

import {
  FetchMaterialsCacheKey,
  FetchMaterialsFilters,
  FetchMaterialsGqlQuery,
  FetchMaterialsLimit,
  FetchMaterialsSort,
  MaterialNanoID
} from '../../materialsTypes';
import {
  FetchCacheItemSources,
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  UUID
} from '../../../../types';

import {
  CacheInfiniteIndexQueryBaseNodeType,
  CacheInfiniteIndexQueryWithFetchItemOptions,
  CacheInfiniteIndexQueryWithoutFetchItemOptions,
  useCacheInfiniteIndexQuery
} from '../../../common/reactQuery/hooks/useCacheInfiniteIndexQuery';

import {
  INITIAL_MATERIALS_FILTERS,
  INITIAL_MATERIALS_LIMIT,
  INITIAL_MATERIALS_SORT,
  MaterialsPermissions
} from '../../materialsConstants';

type CacheMaterialsWithPrefetchItemOptions =
  CacheInfiniteIndexQueryWithFetchItemOptions;

type CacheMaterialsWithoutPrefetchItemOptions =
  CacheInfiniteIndexQueryWithoutFetchItemOptions;

interface CacheMaterialsDefaultOptions {
  additionalParams?: Record<string, unknown>;
  cacheKey: FetchMaterialsCacheKey;
  query: FetchMaterialsGqlQuery;
  cacheQuery?: FetchMaterialsGqlQuery;
  defaultApi?: 'api' | 'read_api' | 'fin_api' | 'cache_api';
  initialFilters?: FetchMaterialsFilters;
  initialSort?: FetchMaterialsSort;
  initialLimit?: FetchMaterialsLimit;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
  };
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
}

type CacheMaterialsOptions = CacheMaterialsDefaultOptions &
  (
    | CacheMaterialsWithoutPrefetchItemOptions
    | CacheMaterialsWithPrefetchItemOptions
  );

const scope = 'materials';

function useCacheMaterials<
  MaterialItemType extends CacheInfiniteIndexQueryBaseNodeType,
  MaterialMetaType = unknown,
  MaterialAggregationsType = unknown
>({
  additionalParams,
  cacheKey,
  fetchCacheItemSource = FetchCacheItemSources.ES,
  fetchItemCacheKey,
  fetchItemQuery,
  fetchCacheItemQuery,
  initialFilters = INITIAL_MATERIALS_FILTERS,
  initialLimit = INITIAL_MATERIALS_LIMIT,
  initialSort = INITIAL_MATERIALS_SORT,
  options = {},
  query,
  cacheQuery,
  defaultApi = 'read_api',
  source = FetchCacheItemsSources.ES,
  trackTotalHits = false
}: CacheMaterialsOptions) {
  const {
    data,
    items,
    itemsError,
    itemsTotalCount,
    isFetched,
    isLoading,
    isFetchingNextPage,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    hasNextPage,
    updateItemCache,
    loadMoreItems,
    filterItems,
    changeItemsFilters,
    clearItemsFilters,
    clearItemsFiltersPersistInitial,
    sortItems,
    prefetchItem,
    limitItems,
    aggregations
  } = useCacheInfiniteIndexQuery<
    MaterialItemType,
    MaterialMetaType,
    MaterialAggregationsType
  >({
    action: MaterialsPermissions.READ_MATERIALS_INDEX_ES_QUERY,
    additionalParams,
    cacheKey,
    cacheQuery,
    defaultApi,
    fetchCacheItemQuery,
    fetchCacheItemSource,
    fetchItemCacheKey,
    fetchItemQuery,
    initialFilters,
    initialLimit,
    initialSort,
    options,
    query,
    scope,
    source,
    trackTotalHits
  });

  return {
    materialsData: data,
    materials: items,
    materialsError: itemsError,
    materialsTotalCount: itemsTotalCount,
    materialsFetched: isFetched,
    materialsLoading: isLoading,
    materialsFetchingNextPage: isFetchingNextPage,
    materialsIsPlaceholderData: isPlaceholderData,
    materialsFilters: currentFilters,
    materialsSort: currentSort as FetchMaterialsSort,
    materialsPage: currentPage,
    materialsLimit: currentLimit,
    hasNextMaterialsPage: hasNextPage,
    updateMaterialCache: updateItemCache,
    loadMoreMaterials: loadMoreItems,
    filterMaterials: filterItems,
    changeMaterialsFilters: changeItemsFilters,
    clearMaterialsFilters: clearItemsFilters,
    clearMaterialsFiltersPersistInitial: clearItemsFiltersPersistInitial,
    sortMaterials: sortItems,
    limitMaterials: limitItems,
    prefetchMaterial: useCallback(
      (materialNanoId: MaterialNanoID) => {
        const nanoId = materialNanoId as unknown as UUID;
        prefetchItem(nanoId);
      },
      [prefetchItem]
    ),
    materialsAggregations: aggregations
  };
}

export default useCacheMaterials;
